import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { FocusRef } from '../../components';
import { useTableStoreValue } from '../TableContext';
import { createFocusHandle, createOnBlur } from './focusHandlers';
import { FilterInputProps } from './FilterInputProps';
import { Select } from 'antd';
import { FilterOption } from '../renderers';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './SelectFilterInput.translations.json';
import { OptionType } from '../../queryClient';
import styled from 'styled-components';

const { addTranslations, useTranslation } = i18nNsFactory(
  'select-filter-input'
);
addTranslations(translations);

const EMPTY_OPTIONS: FilterOption[] = [];

function useSelectFilterOptions(columnId: string) {
  const [options] = useTableStoreValue('options');
  const columnOptions = options?.[columnId];

  return useMemo(
    () =>
      (columnOptions ?? EMPTY_OPTIONS).map(
        (option: FilterOption) =>
          ({
            label: option.name,
            value: option.id,
          } as OptionType)
      ),
    [columnOptions]
  );
}

export const SelectFilterInput = forwardRef<FocusRef, FilterInputProps>(
  (
    {
      onBlur,
      header: {
        column: { id: columnId, getFilterValue, setFilterValue },
      },
    },
    ref
  ) => {
    const id = `${columnId}-filter-input`;
    const options = useSelectFilterOptions(columnId);
    const { t } = useTranslation();

    useImperativeHandle(ref, () => createFocusHandle(id));
    const handleOnBlur = useMemo(
      () => createOnBlur<HTMLDivElement>(id, onBlur),
      [id, onBlur]
    );
    const handleChange = useCallback(
      (value?: string | number) => {
        setFilterValue(value?.toString() ?? null);
      },
      [setFilterValue]
    );
    const value = getFilterValue() as string | null;

    return (
      <SelectWrapper
        id={id}
        allowClear
        className={id}
        defaultOpen
        styles={{
          popup: { root: { width: 'max-content', maxWidth: '300px' } },
        }}
        filterOption={(input, option) =>
          Boolean(option?.label?.toLowerCase().includes(input.toLowerCase()))
        }
        placeholder={t('placeholder')}
        options={options}
        onBlur={handleOnBlur}
        onChange={handleChange}
        showSearch
        value={value}
      />
    );
  }
);

const SelectWrapper = styled(Select<any, any>)`
  width: 200px;
`;
